/**
 * A tapped bottle alert opens that baby's log: when the alert opened Babia
 * (`/?baby=…`, which SelectedBabyProvider already chose from, then removed
 * from the address bar), and when Babia was already open (the service
 * worker's message, sw/sw.ts). The app answers that message once it opened
 * the baby; a window that can't (still loading, or signed out) never does,
 * and the worker loads the alert's page there instead.
 */
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { bottleCheck, OPEN_BABY, type OpenBabyMessage } from "../sw/notification.ts";
import { track } from "./analytics.ts";
import { alertFromSearch } from "./push-state.ts";
import { useSelectedBaby } from "./selected-baby.tsx";

/** Ask the browser to look for a newer worker, on a device that already has one. */
function refreshServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;
  void navigator.serviceWorker
    .getRegistration("/")
    .then((registration) => registration?.update())
    .catch(() => undefined);
}

/** Inside the app shell and its baby selection (components/layout.tsx). */
export function usePushAlerts(): void {
  const { setBabyId } = useSelectedBaby();
  const navigate = useNavigate();

  useEffect(() => {
    refreshServiceWorker();
    if (window.location.pathname !== "/") return;
    const landing = alertFromSearch(window.location.search);
    if (!landing.babyId && !landing.fromPush) return;
    if (landing.fromPush) track("push opened", { check: landing.check, was_open: false });
    void navigate({ to: "/", replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const onMessage = (event: MessageEvent) => {
      const data = event.data as Partial<OpenBabyMessage> | null;
      if (data?.type !== OPEN_BABY) return;
      if (typeof data.babyId === "string") setBabyId(data.babyId);
      track("push opened", { check: bottleCheck(data.check), was_open: true });
      void navigate({ to: "/" });
      event.ports[0]?.postMessage("opened");
    };
    navigator.serviceWorker.addEventListener("message", onMessage);
    return () => navigator.serviceWorker.removeEventListener("message", onMessage);
  }, [setBabyId, navigate]);
}
